/**
 * Design system component: StatusDot
 *
 * 8×8px filled circle that communicates item availability at a glance.
 * Colours and an optional glow ring are determined by loan state:
 *   - `available`  → green with green glow (item is with its owner)
 *   - `with-you`   → amber with amber glow (current user is the borrower)
 *   - `on-loan`    → muted grey, no glow (loaned to someone else)
 */
import { memo } from 'react';
import { T } from '@/lib/tokens';

type StatusDotState = 'available' | 'with-you' | 'on-loan';

interface StatusDotProps {
  state: StatusDotState;
}

export const StatusDot = memo(function StatusDot({ state }: StatusDotProps) {
  const colour = state === 'available' ? T.success : state === 'with-you' ? T.warning : T.textMuted;

  const shadow =
    state === 'available'
      ? `0 0 0 3px ${T.successSoft}`
      : state === 'with-you'
        ? `0 0 0 3px ${T.warningSoft}`
        : 'none';

  const label = state === 'available' ? 'Available' : state === 'with-you' ? 'With you' : 'On loan';

  return (
    <span role="img" aria-label={label}>
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          flexShrink: 0,
          background: colour,
          boxShadow: shadow,
        }}
      />
    </span>
  );
});
